import React, { useContext, useMemo, useState } from "react";
import { applyPathPrefix, getLanguagePathPrefix } from "../../shared/localeRouting";
import Head from "next/head";
import LegacySpaApp from "../LegacySpaApp";
import { getStaticRoutePaths } from "../../shared/staticRoutePaths";
import { getTrademarkNotice } from "../../shared/redesignTemplateRules";

/** Tiền tố ngôn ngữ (/en, /fr ...) cho link trong markup SSR fallback — worker dùng prefixRootRelativeHrefs cho markup của nó. */
const LocalePrefixContext = React.createContext("");
const useLocalHref = () => {
  const prefix = useContext(LocalePrefixContext);
  return (href) => {
    const value = String(href || "");
    if (!prefix || !value.startsWith("/") || value.startsWith("//")) return value;
    const [pathPart, ...rest] = value.split(/(?=[?#])/);
    return `${applyPathPrefix(pathPart, prefix)}${rest.join("")}`;
  };
};

const ClientAppLayer = ({ onMounted }) => {
  const handleReady = () => {
    const workerFallback = document.getElementById("worker-seo-fallback");
    if (workerFallback) workerFallback.remove();
    if (typeof onMounted === "function") onMounted();
  };
  return <LegacySpaApp onReady={handleReady} />;
};

const hasSSRMetadata = (metadata) =>
  Boolean(metadata && typeof metadata === "object" && Object.keys(metadata).length > 0);

const formatDateTime = (dateTime) => {
  if (!dateTime) return "";
  const normalized = new Date(dateTime);
  if (Number.isNaN(normalized.getTime())) return "";
  return normalized.toLocaleDateString("en-GB");
};

const buildRobotsMetaContent = (robots) => {
  if (!robots || typeof robots !== "object") return "index,follow";
  const directives = [];
  directives.push(robots.index === false ? "noindex" : "index");
  directives.push(robots.follow === false ? "nofollow" : "follow");
  if (robots.maxSnippet !== undefined) directives.push(`max-snippet:${robots.maxSnippet}`);
  if (robots.maxImagePreview) directives.push(`max-image-preview:${robots.maxImagePreview}`);
  if (robots.maxVideoPreview !== undefined) directives.push(`max-video-preview:${robots.maxVideoPreview}`);
  return directives.join(",");
};

const toBcp47Language = (language) => {
  const normalized = String(language || "en").trim();
  if (normalized === "mm") return "my";
  if (normalized === "tl") return "fil";
  return normalized;
};

const normalizeJsonLd = (jsonLd) => {
  if (!jsonLd) return [];
  if (Array.isArray(jsonLd)) {
    return jsonLd.filter((item) => item && typeof item === "object" && Object.keys(item).length > 0);
  }
  if (typeof jsonLd === "object" && Object.keys(jsonLd).length > 0) return [jsonLd];
  return [];
};

const collectSchemaTypes = (jsonLdList) => {
  const types = new Set();

  const appendType = (value) => {
    if (!value) return;
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (typeof item === "string" && item.trim()) types.add(item.trim());
      });
      return;
    }
    if (typeof value === "string" && value.trim()) types.add(value.trim());
  };

  const walk = (node) => {
    if (!node || typeof node !== "object") return;
    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }
    appendType(node["@type"]);
    if (Array.isArray(node["@graph"])) {
      node["@graph"].forEach(walk);
    }
    Object.keys(node).forEach((key) => {
      if (key === "@context") return;
      walk(node[key]);
    });
  };

  jsonLdList.forEach((item) => walk(item));

  return types;
};

const stripHtml = (html) =>
  String(html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const decodeHtmlEntities = (text) =>
  String(text || "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">");

const toSchemaDescription = (item, maxLength = 260) => {
  const preferred = String(item?.briefText || "").trim();
  const fallback = stripHtml(item?.briefContent || "");
  const normalized = decodeHtmlEntities(preferred || fallback).replace(/\s+/g, " ").trim();
  if (!normalized) return "";
  return normalized.length > maxLength ? `${normalized.slice(0, maxLength).trim()}...` : normalized;
};

const toAbsoluteUrl = (href, canonical) => {
  if (!href) return canonical || "https://qrcode-gen.com/";
  if (/^https?:\/\//i.test(href)) return href;
  try {
    const base = canonical ? new URL(canonical) : new URL("https://qrcode-gen.com/");
    return new URL(href, `${base.protocol}//${base.host}`).toString();
  } catch (_error) {
    return href;
  }
};

const buildServerFallbackJsonLd = ({ serverContent, canonical, language, siteName }) => {
  if (!serverContent) return [];
  const list = [];
  const inLanguage = toBcp47Language(language || "vi");

  if (serverContent.type === "guide-seo") {
    list.push({
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: serverContent.title || "QR Code Guide",
      description: serverContent.description || "",
      url: canonical,
      inLanguage
    });

    /** FAQ what-is-qr được gộp vào metadata.jsonLd trong getServerSideProps để chắc chắn có trong HTML tĩnh. */

    return list;
  }

  if (Array.isArray(serverContent.faq?.items) && serverContent.faq.items.length > 0) {
    list.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      inLanguage,
      mainEntity: serverContent.faq.items.map((item) => ({
        "@type": "Question",
        name: item.question || "",
        acceptedAnswer: {
          "@type": "Answer",
          text: item.answerText || stripHtml(item.answerHtml || "")
        }
      }))
    });
  }

  if (serverContent.type === "redesign-page") {
    if (serverContent.schemaType === "SoftwareApplication") {
      list.push({
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: serverContent.title,
        description: serverContent.description,
        url: canonical,
        image: toAbsoluteUrl(serverContent.image, canonical),
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web"
      });
    } else if (serverContent.schemaType === "Service") {
      list.push({
        "@context": "https://schema.org",
        "@type": "Service",
        name: serverContent.title,
        description: serverContent.description,
        url: canonical,
        provider: {
          "@type": "Organization",
          name: siteName || "QR Code Generator",
          url: "https://qrcode-gen.com/"
        }
      });
    } else if (serverContent.schemaType === "ContactPage") {
      list.push({
        "@context": "https://schema.org",
        "@type": "ContactPage",
        name: serverContent.title,
        description: serverContent.description,
        url: canonical,
        mainEntity: {
          "@type": "Organization",
          name: siteName || "QR Code Generator",
          email: serverContent.contact?.email || "info@ninecode.vn"
        }
      });
    }

    if (serverContent.breadcrumb) {
      const items = [
        {
          "@type": "ListItem",
          position: 1,
          name: serverContent.breadcrumb.homeLabel || "Home",
          item: toAbsoluteUrl("/", canonical)
        }
      ];
      if (serverContent.breadcrumb.parentLabel && serverContent.kind === "product") {
        items.push({
          "@type": "ListItem",
          position: 2,
          name: serverContent.breadcrumb.parentLabel,
          item: toAbsoluteUrl("/#products", canonical)
        });
      }
      items.push({
        "@type": "ListItem",
        position: items.length + 1,
        name: serverContent.breadcrumb.currentLabel || serverContent.title,
        item: canonical
      });
      list.push({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: items
      });
    }
  }

  if (serverContent.type === "blog-list" && Array.isArray(serverContent.items)) {
    const itemListElement = serverContent.items.map((item, index) => {
      const url = toAbsoluteUrl(item.href || `/blog/${item.slug || ""}`, canonical);
      return {
        "@type": "ListItem",
        position: index + 1,
        url,
        item: {
          "@type": "NewsArticle",
          headline: item.title || "",
          description: toSchemaDescription(item),
          image: item.image ? [item.image] : undefined,
          datePublished: item.createdAt || undefined,
          inLanguage,
          mainEntityOfPage: url,
          publisher: {
            "@type": "Organization",
            name: siteName || "QR Code Generator"
          }
        }
      };
    });

    list.push({
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: serverContent.latestTitle || serverContent.title || "Latest articles",
      inLanguage,
      itemListOrder: "https://schema.org/ItemListOrderAscending",
      numberOfItems: itemListElement.length,
      itemListElement
    });
  }

  if (serverContent.type === "content-list") {
    const items = Array.isArray(serverContent.items) ? serverContent.items : [];
    list.push({
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: serverContent.title || "Use cases",
      description: serverContent.description || "",
      inLanguage,
      numberOfItems: items.length,
      itemListElement: items.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        url: toAbsoluteUrl(item.href || `${serverContent.basePath}/${item.slug}`, canonical),
        name: item.title || ""
      }))
    });
    list.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: serverContent.homeLabel || "Home",
          item: toAbsoluteUrl("/", canonical)
        },
        {
          "@type": "ListItem",
          position: 2,
          name: serverContent.eyebrow || serverContent.title || "Use cases",
          item: canonical
        }
      ]
    });
  }

  return list;
};

const getSsrShellCopy = (language) => {
  const normalizedLanguage = String(language || "en").toLowerCase();
  const isJapanese = normalizedLanguage === "ja" || normalizedLanguage.startsWith("ja-");
  const isThai = normalizedLanguage === "th" || normalizedLanguage.startsWith("th-");
  const staticRoutes = getStaticRoutePaths(normalizedLanguage);
  if (normalizedLanguage === "vi") {
    return {
      navAriaLabel: "Điều hướng chính",
      whatIsHref: "/what-is-qr-code",
      whatIsLabel: "Mã QR là gì?",
      serviceHref: staticRoutes.servicePack,
      serviceLabel: "Gói dịch vụ",
      blogHref: "/blog",
      blogLabel: "Blog",
      contactHref: staticRoutes.contact,
      contactLabel: "Liên hệ",
      footerHomeLabel: "Trang chủ",
      footerTermsHref: staticRoutes.terms,
      footerTermsLabel: "Điều khoản",
      footerPrivacyHref: staticRoutes.privacy,
      footerPrivacyLabel: "Chính sách",
      footerBlogLabel: "Blog"
    };
  }

  if (isJapanese) {
    return {
      navAriaLabel: "メインナビゲーション",
      whatIsHref: "/what-is-qr-code",
      whatIsLabel: "QRコードとは",
      serviceHref: staticRoutes.servicePack,
      serviceLabel: "料金プラン",
      blogHref: "/blog",
      blogLabel: "ブログ",
      contactHref: staticRoutes.contact,
      contactLabel: "お問い合わせ",
      footerHomeLabel: "ホーム",
      footerTermsHref: staticRoutes.terms,
      footerTermsLabel: "利用規約",
      footerPrivacyHref: staticRoutes.privacy,
      footerPrivacyLabel: "プライバシーポリシー",
      footerBlogLabel: "ブログ",
      trademarkNotice: "QRコードは株式会社デンソーウェーブの登録商標です。"
    };
  }

  if (isThai) {
    return {
      navAriaLabel: "เมนูหลัก",
      whatIsHref: "/what-is-qr-code",
      whatIsLabel: "QR Code คืออะไร?",
      serviceHref: staticRoutes.servicePack,
      serviceLabel: "แพ็กเกจ",
      blogHref: "/blog",
      blogLabel: "บทความ",
      contactHref: staticRoutes.contact,
      contactLabel: "ติดต่อเรา",
      footerHomeLabel: "หน้าแรก",
      footerTermsHref: staticRoutes.terms,
      footerTermsLabel: "ข้อกำหนดการใช้งาน",
      footerPrivacyHref: staticRoutes.privacy,
      footerPrivacyLabel: "นโยบายความเป็นส่วนตัว",
      footerBlogLabel: "บทความ",
      trademarkNotice: "QR Code เป็นเครื่องหมายการค้าจดทะเบียนของ DENSO WAVE INCORPORATED"
    };
  }

  return {
    navAriaLabel: "Primary",
    whatIsHref: "/what-is-qr-code",
    whatIsLabel: "What is QR code?",
    serviceHref: staticRoutes.servicePack,
    serviceLabel: "Service Packages",
    blogHref: "/blog",
    blogLabel: "Blog",
    contactHref: staticRoutes.contact,
    contactLabel: "Contact",
    footerHomeLabel: "Home",
    footerTermsHref: staticRoutes.terms,
    footerTermsLabel: "Terms",
    footerPrivacyHref: staticRoutes.privacy,
    footerPrivacyLabel: "Privacy",
    footerBlogLabel: "Blog"
  };
};

const SsrShell = ({ children, language }) => {
  const lh = useLocalHref();
  const copy = getSsrShellCopy(language);
  const trademarkNotice = getTrademarkNotice(language, copy.trademarkNotice);
  return (
  <div data-ssr-shell="qrcode-gen">
    <header
      style={{
        borderBottom: "1px solid #e5e7eb",
        background: "#fff"
      }}
    >
      <div
        className="container"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "14px 16px"
        }}
      >
        <a href={lh("/")} style={{ fontWeight: 700, color: "#111", textDecoration: "none" }}>
          QRCODE-GEN
        </a>
        <nav aria-label={copy.navAriaLabel}>
          <a
            href={lh(copy.whatIsHref)}
            style={{
              marginRight: 16,
              color: "#111",
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
              minHeight: 44,
              padding: "8px 0"
            }}
          >
            {copy.whatIsLabel}
          </a>
          <a
            href={lh(copy.serviceHref)}
            style={{
              marginRight: 16,
              color: "#111",
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
              minHeight: 44,
              padding: "8px 0"
            }}
          >
            {copy.serviceLabel}
          </a>
          <a
            href={lh(copy.blogHref)}
            style={{
              marginRight: 16,
              color: "#111",
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
              minHeight: 44,
              padding: "8px 0"
            }}
          >
            {copy.blogLabel}
          </a>
          <a
            href={lh(copy.contactHref)}
            style={{
              color: "#111",
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
              minHeight: 44,
              padding: "8px 0"
            }}
          >
            {copy.contactLabel}
          </a>
        </nav>
      </div>
    </header>
    {children}
    <footer style={{ borderTop: "1px solid #e5e7eb", marginTop: 40, padding: "20px 0", background: "#fff" }}>
      <div className="container" style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <a href={lh("/")} style={{ color: "#111", textDecoration: "none", minHeight: 44, display: "inline-flex", alignItems: "center", padding: "8px 0" }}>
          {copy.footerHomeLabel}
        </a>
        <a href={lh(copy.footerTermsHref)} style={{ color: "#111", textDecoration: "none", minHeight: 44, display: "inline-flex", alignItems: "center", padding: "8px 0" }}>
          {copy.footerTermsLabel}
        </a>
        <a href={lh(copy.footerPrivacyHref)} style={{ color: "#111", textDecoration: "none", minHeight: 44, display: "inline-flex", alignItems: "center", padding: "8px 0" }}>
          {copy.footerPrivacyLabel}
        </a>
        <a href={lh("/blog")} style={{ color: "#111", textDecoration: "none", minHeight: 44, display: "inline-flex", alignItems: "center", padding: "8px 0" }}>
          {copy.footerBlogLabel}
        </a>
      </div>
      <p className="container" style={{ marginTop: 8, color: "#606a5a", fontSize: 12 }}>
        {trademarkNotice}
      </p>
    </footer>
  </div>
  );
};

const RedesignItems = ({ items }) => {
  const lh = useLocalHref();
  if (!Array.isArray(items) || items.length === 0) return null;
  return items.map((item, index) => (
    <article key={`${item?.title || "item"}-${index}`} style={{ marginBottom: 16 }}>
      {item?.title ? (
        <h3>
          {item?.href ? <a href={lh(item.href)}>{item.title}</a> : item.title}
        </h3>
      ) : null}
      {item?.description ? <p>{item.description}</p> : null}
      {Array.isArray(item?.facts) && item.facts.length > 0 ? (
        <ul>
          {item.facts.map((fact) => <li key={fact}>{fact}</li>)}
        </ul>
      ) : null}
    </article>
  ));
};

const RedesignSections = ({ sections }) => {
  const lh = useLocalHref();
  if (!Array.isArray(sections) || sections.length === 0) return null;
  return sections.map((section, index) => (
    <section key={`${section?.heading || "section"}-${index}`} style={{ marginTop: 28 }}>
      {section?.eyebrow ? <p>{section.eyebrow}</p> : null}
      {section?.heading ? <h2>{section.heading}</h2> : null}
      {section?.description ? <p>{section.description}</p> : null}
      <RedesignItems items={section?.items} />
      {Array.isArray(section?.lists)
        ? section.lists.map((list) => (
            <div key={list.title}>
              <h3>{list.title}</h3>
              <ul>{(list.items || []).map((item) => <li key={item}>{item}</li>)}</ul>
            </div>
          ))
        : null}
      {Array.isArray(section?.tableRows) && section.tableRows.length > 0 ? (
        <table>
          <thead>
            <tr>{(section.tableHeaders || []).map((cell) => <th scope="col" key={cell}>{cell}</th>)}</tr>
          </thead>
          <tbody>
            {section.tableRows.map((row, rowIndex) => (
              <tr key={`row-${rowIndex}`}>
                {row.map((cell, cellIndex) =>
                  cellIndex === 0
                    ? <th scope="row" key={`${cell}-${cellIndex}`}>{cell}</th>
                    : <td key={`${cell}-${cellIndex}`}>{cell}</td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </section>
  ));
};

const RedesignFaq = ({ faq }) => {
  if (!Array.isArray(faq?.items) || faq.items.length === 0) return null;
  return (
    <section style={{ marginTop: 28 }}>
      <h2>{faq.title || "FAQ"}</h2>
      {faq.intro ? <p>{faq.intro}</p> : null}
      {faq.items.map((item) => (
        <article key={item.id || item.question}>
          <h3>{item.question}</h3>
          <p>{item.answerText || stripHtml(item.answerHtml || "")}</p>
        </article>
      ))}
    </section>
  );
};

const RedesignRelatedLinks = ({ links }) => {
  const lh = useLocalHref();
  if (!Array.isArray(links) || links.length === 0) return null;
  return (
    <nav aria-label="Related pages" style={{ marginTop: 28 }}>
      <ul>
        {links.map((item) => (
          <li key={`${item.href}-${item.label}`}><a href={lh(item.href)}>{item.label}</a></li>
        ))}
      </ul>
    </nav>
  );
};

const RedesignCta = ({ cta }) => {
  const lh = useLocalHref();
  if (!cta?.title && !cta?.label) return null;
  return (
    <section style={{ marginTop: 28 }}>
      {cta.title ? <h2>{cta.title}</h2> : null}
      {cta.description ? <p>{cta.description}</p> : null}
      <a href={lh(cta.href || "/")}>{cta.label || cta.title}</a>
    </section>
  );
};

const GuideContentSections = ({ content }) => {
  const lh = useLocalHref();
  const renderSubsection = (section, index, Heading = "h2") => (
    <section key={`${section?.heading || "section"}-${index}`} style={{ marginBottom: 16 }}>
      <Heading style={{ margin: "0 0 8px" }}>{section?.heading}</Heading>
      {(section?.paragraphs || []).map((paragraph, paragraphIndex) => (
        <p key={`${section?.heading}-p-${paragraphIndex}`} style={{ margin: "0 0 8px" }}>
          {paragraph}
        </p>
      ))}
      {section?.illustrations?.length > 0 ? (
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(min(100%,260px),1fr))",gap:16,margin:"16px 0"}}>
          {section.illustrations.map(item => (
            <figure key={item.src} style={{margin:0,maxWidth:520}}>
              <img src={item.src} alt={item.alt} width={item.width} height={item.height} loading="lazy" decoding="async" style={{width:"100%",height:"auto",borderRadius:14}} />
              {item.title ? <figcaption><h3>{item.title}</h3><p>{item.description}</p></figcaption> : null}
            </figure>
          ))}
        </div>
      ) : null}
      {Array.isArray(section?.bullets) && section.bullets.length > 0 ? (
        <ul>
          {section.bullets.map((point) => <li key={point}>{point}</li>)}
        </ul>
      ) : null}
      {Array.isArray(section?.steps) && section.steps.length > 0 ? (
        <ol>
          {section.steps.map((step) => (
            <li key={step.title}><strong>{step.title}</strong>{step.description ? ` — ${step.description}` : ""}</li>
          ))}
        </ol>
      ) : null}
      {section?.href && section?.cta ? <p><a href={lh(section.href)}>{section.cta}</a></p> : null}
    </section>
  );

  if (Array.isArray(content?.chapters) && content.chapters.length > 0) {
    return content.chapters.map((chapter, chapterIndex) => (
      <section key={`${chapter.heading}-${chapterIndex}`} style={{ marginBottom: 24 }}>
        <h2>{chapter.heading}</h2>
        {(chapter.sections || []).map((section, sectionIndex) =>
          renderSubsection(section, sectionIndex, "h3")
        )}
      </section>
    ));
  }

  return (content?.sections || []).map((section, index) => renderSubsection(section, index));
};

const ServerRenderedFallback = ({ content }) => {
  const lh = useLocalHref();
  if (!content) return null;

  if (content.type === "home-redesign") {
    return (
      <SsrShell language={content.language}>
        <main data-ssr-page="home-redesign" className="container" style={{ paddingTop: 32, paddingBottom: 20 }}>
          <header style={{ maxWidth: 900 }}>
            <h1>{content.displayTitle || content.title}</h1>
            <p>{content.description}</p>
          </header>
          <section aria-label="QRCode GEN benefits" style={{ marginTop: 28 }}>
            {content.commitmentsTitle ? <h2>{content.commitmentsTitle}</h2> : null}
            <RedesignItems items={content.commitments} />
          </section>
          <RedesignSections sections={content.sections} />
          <RedesignFaq faq={content.faq} />
          <RedesignCta cta={content.cta} />
          <RedesignRelatedLinks links={content.relatedLinks} />
        </main>
      </SsrShell>
    );
  }

  if (content.type === "redesign-page") {
    return (
      <SsrShell language={content.language}>
        <main data-ssr-page={`redesign-${content.kind || "page"}`} className="container" style={{ paddingTop: 28, paddingBottom: 28 }}>
          <nav aria-label="Breadcrumb">
            <a href={lh("/")}>{content?.breadcrumb?.homeLabel || "Home"}</a>
            {content?.breadcrumb?.parentLabel ? <span> / {content.breadcrumb.parentLabel}</span> : null}
            <span> / {content?.breadcrumb?.currentLabel || content.title}</span>
          </nav>
          <article>
            <header>
              {content.eyebrow ? <p>{content.eyebrow}</p> : null}
              <h1>{content.title}</h1>
              <p>{content.description}</p>
              {Array.isArray(content.facts) && content.facts.length > 0 ? (
                <ul>{content.facts.map((fact) => <li key={fact}>{fact}</li>)}</ul>
              ) : null}
            </header>
            <RedesignSections sections={content.sections} />
            {content.contact?.email ? (
              <address>
                <strong>{content.contact.emailLabel || "Email"}</strong>:{" "}
                <a href={`mailto:${content.contact.email}`}>{content.contact.email}</a>
              </address>
            ) : null}
            <RedesignFaq faq={content.faq} />
            <RedesignCta cta={content.cta} />
            <RedesignRelatedLinks links={content.relatedLinks} />
          </article>
        </main>
      </SsrShell>
    );
  }

  if (content.type === "home-seo") {
    return (
      <SsrShell language={content.language}>
        <main data-ssr-page="home-seo" className="container" style={{ paddingTop: 32, paddingBottom: 20 }}>
          <section style={{ maxWidth: 900 }}>
            <h1 style={{ marginBottom: 12 }}>{content.heroTitle || content.title}</h1>
            <p style={{ marginBottom: 16 }}>{content.intro || content.description}</p>
            <ul style={{ display: "flex", gap: 18, flexWrap: "wrap", paddingLeft: 0, listStyle: "none" }}>
              {(content.links || []).map((item) => (
                <li key={item.href}>
                  <a href={lh(item.href)}>{item.label}</a>
                </li>
              ))}
            </ul>
          </section>
          {Array.isArray(content.applications) && content.applications.length > 0 ? (
            <section
              aria-label={String(content.language || "").toLowerCase() === "vi" ? "Ứng dụng QR" : "QR applications"}
              style={{ marginTop: 28 }}
            >
              {content.applications.map((item) => (
                <article
                  key={item.key || item.title}
                  style={{
                    marginBottom: 20,
                    border: "1px solid #e5e7eb",
                    borderRadius: 12,
                    padding: "16px 18px",
                    background: "#fff"
                  }}
                >
                  <h2 style={{ margin: "0 0 8px" }}>{item.title}</h2>
                  <p style={{ margin: "0 0 10px" }}>{item.description}</p>
                  {Array.isArray(item.highlights) && item.highlights.length > 0 ? (
                    <ul style={{ margin: "0 0 10px", paddingLeft: 20 }}>
                      {(item.highlights || []).map((point, pointIdx) => (
                        <li key={`${item.key || "app"}-${pointIdx}`} style={{ marginBottom: 4 }}>
                          {point}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  {item.href ? <a href={lh(item.href)}>{item.cta || item.title || "Details"}</a> : null}
                </article>
              ))}
            </section>
          ) : null}
          {Array.isArray(content.faq?.items) && content.faq.items.length > 0 ? (
            <section
              aria-label={content.faq.title || "FAQ"}
              style={{ marginTop: 30, maxWidth: 1140, marginLeft: "auto", marginRight: "auto", paddingLeft: 12, paddingRight: 12 }}
            >
              <h2 style={{ margin: "0 0 8px" }}>{content.faq.title || "FAQ"}</h2>
              {content.faq.intro ? <p style={{ margin: "0 0 16px", color: "#5f6368", lineHeight: 1.65 }}>{content.faq.intro}</p> : null}
              <div className="row" style={{ marginLeft: -12, marginRight: -12 }}>
                {[0, 1].map((col) => {
                  const colItems = content.faq.items.filter((_, idx) => idx % 2 === col);
                  return (
                    <div className="col-12 col-lg-6" key={`faq-col-${col}`} style={{ paddingLeft: 12, paddingRight: 12 }}>
                      {colItems.map((faq) => (
                        <details
                          key={faq.id || faq.question}
                          style={{
                            marginBottom: 14,
                            border: "1px solid #e8ebee",
                            borderRadius: 12,
                            background: "#fff",
                            overflow: "hidden",
                            alignSelf: "start",
                            boxShadow: "0 2px 8px rgba(17,24,39,0.05)",
                            transition: "border-color 0.2s ease, box-shadow 0.2s ease"
                          }}
                        >
                          <summary
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 8,
                              padding: "16px 18px",
                              cursor: "pointer",
                              listStyle: "none",
                              fontWeight: 600,
                              fontSize: 17,
                              lineHeight: 1.45,
                              color: "#1f2937",
                              userSelect: "none"
                            }}
                          >
                            <span style={{ color: "#41b60b", fontWeight: 700, minWidth: 32, flexShrink: 0 }} aria-hidden>
                              {faq.index ? `${faq.index}.` : ""}
                            </span>
                            <span style={{ flex: 1 }}>{faq.question}</span>
                            <span
                              aria-hidden
                              style={{
                                flexShrink: 0,
                                width: 9,
                                height: 9,
                                marginTop: 6,
                                borderRight: "2px solid #94a3b8",
                                borderBottom: "2px solid #94a3b8",
                                transform: "rotate(45deg)"
                              }}
                            />
                          </summary>
                          <div
                            style={{
                              padding: "0 18px 16px",
                              borderTop: "1px solid #eef1f5",
                              color: "#4b5563",
                              fontSize: 15,
                              lineHeight: 1.75,
                              background: "linear-gradient(180deg, #fcfcfd 0%, #fff 12px)"
                            }}
                            dangerouslySetInnerHTML={{ __html: faq.answerHtml || "" }}
                          />
                        </details>
                      ))}
                    </div>
                  );
                })}
              </div>
            </section>
          ) : null}
          {Array.isArray(content.latestNews?.items) && content.latestNews.items.length > 0 ? (
            <section aria-label={content.latestNews.title || "Latest news"} style={{ marginTop: 32 }}>
              <h2 style={{ margin: "0 0 12px" }}>{content.latestNews.title || "Latest news"}</h2>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 14 }}>
                {content.latestNews.items.map((item) => (
                  <article
                    key={item.id || item.slug}
                    style={{
                      border: "1px solid #e5e7eb",
                      borderRadius: 12,
                      overflow: "hidden",
                      background: "#fff"
                    }}
                  >
                    {item.image ? (
                      <a href={lh(item.href || `/blog/${item.slug}`)} aria-label={item.title || "News detail"}>
                        <img
                          src={item.image}
                          alt={item.title || ""}
                          width="640"
                          height="360"
                          loading="lazy"
                          decoding="async"
                          style={{ width: "100%", height: "auto", display: "block" }}
                        />
                      </a>
                    ) : null}
                    <div style={{ padding: "14px 14px 16px" }}>
                      <h3 style={{ margin: "0 0 8px", fontSize: 18, lineHeight: 1.45 }}>
                        <a href={lh(item.href || `/blog/${item.slug}`)}>{item.title || "Untitled"}</a>
                      </h3>
                      <p style={{ color: "#374151", fontSize: 15, lineHeight: 1.7, margin: 0 }}>
                        {item.briefText || stripHtml(item.briefContent || "")}
                      </p>
                    </div>
                  </article>
                ))}
              </div>
              <p style={{ marginTop: 14 }}>
                <a href={lh(content.latestNews.viewAllHref || "/blog")}>
                  {content.latestNews.viewAllLabel || "View all"}
                </a>
              </p>
            </section>
          ) : null}
        </main>
      </SsrShell>
    );
  }

  if (content.type === "blog-list") {
    return (
      <SsrShell language={content.language}>
        <main data-ssr-page="blog-list" className="container" style={{ paddingTop: 24, paddingBottom: 24 }}>
          <header>
            <h1>{content.title || "QR Code Blog"}</h1>
            {content.description ? <p>{content.description}</p> : null}
          </header>
          <section aria-label={content.latestTitle || content.title || "Latest articles"}>
            {content.latestTitle ? <h2>{content.latestTitle}</h2> : null}
            {content.items.map((item) => (
              <article key={item.id} style={{ marginBottom: 24 }}>
                {item.image ? (
                  <img
                    src={item.image}
                    alt={item.title || ""}
                    width="800"
                    height="533"
                    loading="lazy"
                    decoding="async"
                    style={{ maxWidth: "100%", height: "auto" }}
                  />
                ) : null}
                {item.createdAt ? <time dateTime={item.createdAt}>{formatDateTime(item.createdAt)}</time> : null}
                <h3>
                  <a href={lh(`/blog/${item.slug}`)}>{item.title}</a>
                </h3>
                <p>{item.briefText || stripHtml(item.briefContent || "")}</p>
              </article>
            ))}
          </section>
        </main>
      </SsrShell>
    );
  }

  if (content.type === "content-detail") return <SsrShell language={content.language}><main className="container" lang={content.language}><h1>{content.title}</h1><div dangerouslySetInnerHTML={{__html: content.content || ''}} /></main></SsrShell>;

  if (content.type === "content-list") {
    const items = Array.isArray(content.items) ? content.items : [];
    return (
      <SsrShell language={content.language}>
        <main data-ssr-page={content.key || "content-list"} className="container" style={{ paddingTop: 24, paddingBottom: 24 }}>
          <nav aria-label="Breadcrumb">
            <a href={lh("/")}>{content.homeLabel || "Home"}</a>
            <span> / {content.eyebrow || content.title}</span>
          </nav>
          <header style={{ marginTop: 18 }}>
            {content.eyebrow ? <p>{content.eyebrow}</p> : null}
            <h1>{content.title}</h1>
            {content.description ? <p>{content.description}</p> : null}
          </header>
          {items.length > 0 ? (
            <section aria-label={content.eyebrow || content.title}>
              {items.map((item) => (
                <article key={item.id || item.slug} style={{ marginBottom: 24 }}>
                  {item.image ? (
                    <a href={lh(item.href || `${content.basePath}/${item.slug}`)} aria-label={item.title}>
                      <img
                        src={item.image}
                        alt={item.title || ""}
                        width="800"
                        height="533"
                        loading="lazy"
                        decoding="async"
                        style={{ maxWidth: "100%", height: "auto" }}
                      />
                    </a>
                  ) : null}
                  <h2>
                    <a href={lh(item.href || `${content.basePath}/${item.slug}`)}>
                      {item.title}
                    </a>
                  </h2>
                  {item.briefText || item.briefContent ? (
                    <p>{item.briefText || stripHtml(item.briefContent)}</p>
                  ) : null}
                  <a href={lh(item.href || `${content.basePath}/${item.slug}`)}>
                    {content.readMoreLabel || "View details"}
                  </a>
                </article>
              ))}
            </section>
          ) : (
            <p>{content.emptyLabel || "Content is being updated."}</p>
          )}
        </main>
      </SsrShell>
    );
  }

  if (content.type === "guide-seo") {
    return (
      <SsrShell language={content.language}>
        <main data-ssr-page="guide-seo" className="container" style={{ paddingTop: 28, paddingBottom: 28 }}>
          <article style={{ maxWidth: 920 }}>
            <nav aria-label="Breadcrumb" style={{ marginBottom: 10, fontSize: 14 }}>
              <a href={lh("/")}>{content?.breadcrumb?.homeLabel || "Home"}</a>
              <span style={{ margin: "0 8px" }}>/</span>
              <span>{content?.breadcrumb?.currentLabel || content.displayTitle || content.title}</span>
            </nav>
            <h1 style={{ margin: "0 0 12px" }}>{content.displayTitle || content.title}</h1>
            <p style={{ margin: "0 0 14px" }}>{content.intro || content.description}</p>
            {content.image ? (
              <img
                src={content.image}
                alt={content.title || "QR Code guide"}
                width="1200"
                height="630"
                loading="eager"
                decoding="async"
                style={{ width: "100%", height: "auto", borderRadius: 12, display: "block", marginBottom: 14 }}
              />
            ) : null}
            <GuideContentSections content={content} />
            {Array.isArray(content.relatedLinks) && content.relatedLinks.length > 0 ? (
              <section aria-label={content.relatedLinksTitle || "Related links"} style={{ marginTop: 8 }}>
                <h2 style={{ margin: "0 0 8px" }}>{content.relatedLinksTitle || "Related links"}</h2>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {content.relatedLinks.map((item) => (
                    <li key={`${content.key || "guide"}-related-${item.href}`} style={{ marginBottom: 6 }}>
                      <a href={lh(item.href)}>{item.label}</a>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
            <p style={{ marginTop: 14 }}>
              <a href={lh(content.ctaHref || "/")}>{content.ctaLabel || "Read guide"}</a>
            </p>
          </article>
        </main>
      </SsrShell>
    );
  }

  if (content.type !== "blog-detail") return null;

  return (
    <SsrShell language={content.language}>
      <main data-ssr-page="blog-detail" className="container" style={{ paddingTop: 24, paddingBottom: 24 }}>
        <article>
          <h1>{content.title}</h1>
          <p>{formatDateTime(content.createdAt)}</p>
          <div dangerouslySetInnerHTML={{ __html: content.content || "" }} />
        </article>
      </main>
    </SsrShell>
  );
};

const CatchAllSlugPage = ({ pathname, metadata, serverContent }) => {
  const localePrefix = getLanguagePathPrefix(metadata?.language || "");
  const useNextHead = hasSSRMetadata(metadata);
  const hasServerContent = Boolean(serverContent);
  const canonical = metadata?.alternates?.canonical || `https://qrcode-gen.com${pathname || "/"}`;
  const languages = metadata?.alternates?.languages || {};
  const og = metadata?.openGraph || {};
  const twitter = metadata?.twitter || {};
  const otherMeta = metadata?.other || {};
  const xDefault = metadata?.alternates?.xDefault;
  const robotsContent = buildRobotsMetaContent(metadata?.robots);
  const jsonLdList = normalizeJsonLd(metadata?.jsonLd);
  const schemaTypesFromMetadata = useMemo(() => collectSchemaTypes(jsonLdList), [jsonLdList]);
  const [showServerContent, setShowServerContent] = useState(hasServerContent);
  const mountClientApp = true;

  const siteName = useMemo(
    () => otherMeta["og:site_name"] || og?.siteName || "QR Code Generator",
    [otherMeta, og?.siteName]
  );

  const serverFallbackJsonLd = useMemo(
    () =>
      buildServerFallbackJsonLd({
        serverContent,
        canonical,
        language: metadata?.language,
        siteName
      }).filter((item) => {
        const fallbackType = item?.["@type"];
        if (!fallbackType || typeof fallbackType !== "string") return true;
        return !schemaTypesFromMetadata.has(fallbackType);
      }),
    [serverContent, canonical, metadata?.language, siteName, schemaTypesFromMetadata]
  );

  return (
    <LocalePrefixContext.Provider value={localePrefix}>
    <>
      {useNextHead ? (
        <Head>
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <title>{metadata?.title || "QR Code GEN - Tạo Mã QR Miễn Phí"}</title>
          <meta name="description" content={metadata?.description || ""} />
          <meta name="robots" content={robotsContent} />
          <meta name="googlebot" content={robotsContent} />
          <meta name="language" content={toBcp47Language(metadata?.language || "vi")} />
          <meta property="og:type" content={og?.type || "website"} />
          <meta property="og:title" content={og?.title || metadata?.title || ""} />
          <meta property="og:description" content={og?.description || metadata?.description || ""} />
          <meta property="og:url" content={og?.url || canonical} />
          <meta property="og:image" content={og?.images?.[0] || ""} />
          <meta property="og:image:alt" content={og?.imageAlt || metadata?.title || ""} />
          <meta property="og:locale" content={og?.locale || "vi_VN"} />
          <meta property="og:site_name" content={siteName} />
          <meta name="twitter:card" content={twitter?.card || "summary_large_image"} />
          <meta name="twitter:url" content={twitter?.url || canonical} />
          <meta name="twitter:title" content={twitter?.title || metadata?.title || ""} />
          <meta name="twitter:description" content={twitter?.description || metadata?.description || ""} />
          <meta name="twitter:image" content={twitter?.images?.[0] || ""} />
          <meta name="twitter:site" content={twitter?.site || "@qrcodegen"} />
          <link rel="canonical" href={canonical} />
          {Object.entries(languages).map(([lang, href]) => (
            <link key={lang} rel="alternate" hrefLang={lang} href={href} />
          ))}
          {xDefault ? <link rel="alternate" hrefLang="x-default" href={xDefault} /> : null}
          {Object.entries(otherMeta)
            .filter(([key]) => key !== "og:site_name")
            .map(([key, value]) => (
              <meta key={key} property={key} content={String(value || "")} />
            ))}
          {jsonLdList.map((item, idx) => (
            <script
              key={`jsonld-${idx}`}
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(item) }}
            />
          ))}
          {serverFallbackJsonLd.map((item, idx) => (
            <script
              key={`jsonld-server-fallback-${idx}`}
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(item) }}
            />
          ))}
        </Head>
      ) : null}
      {showServerContent ? (
        <div className="runtime-seo-fallback" aria-hidden="true">
          <ServerRenderedFallback content={serverContent} />
        </div>
      ) : null}
      {mountClientApp ? (
        <ClientAppLayer
          onMounted={() => {
            if (hasServerContent) setShowServerContent(false);
          }}
        />
      ) : null}
    </>
    </LocalePrefixContext.Provider>
  );
};

export default CatchAllSlugPage;
